import { useCallback, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { tcRunKeys } from '@/queries/useTcRun';
import type { TcRunAppraiseeResponse, TcRunDetailResponse } from '@/api/types/tcRun.types';
import { TcRunAppraiseeMutationQueueContext } from './TcRunAppraiseeMutationQueueContext';

interface ProviderProps {
  tcId: number;
  appraisee: TcRunAppraiseeResponse;
  children: ReactNode;
}

export function TcRunAppraiseeMutationQueueProvider({ tcId, appraisee, children }: ProviderProps) {
  const queryClient = useQueryClient();
  const pendingRef = useRef<Promise<unknown> | null>(null);
  const needsRefreshRef = useRef(false);

  // Keep latest props in refs so callbacks always see current values without being
  // re-created (which would break useCallback-based consumer memoization).
  const tcIdRef = useRef(tcId);
  tcIdRef.current = tcId;
  const appraiseeRef = useRef(appraisee);
  appraiseeRef.current = appraisee;

  const runSerialized = useCallback(
    <T,>(mutationFn: (rowVersion: string) => Promise<T>): Promise<T> => {
      // Snapshot the previous chain head, then synchronously install OUR promise
      // as the new head. Subsequent callers will await this promise and can never
      // slip past at pendingRef === null while our async work is in flight.
      const prev = pendingRef.current;

      const run = async (): Promise<T> => {
        // 1. Wait for the previous mutation to finish (errors already handled by its owner).
        if (prev) {
          try {
            await prev;
          } catch {
            /* handled by the owner + global 409 interceptor */
          }
        }

        // 2. If the previous mutation bumped the rowVersion (or was reset after 409),
        //    refetch the TC detail query — which is what the page hydrates from —
        //    and read the fresh rowVersion.
        let rowVersion = appraiseeRef.current.rowVersion;
        if (needsRefreshRef.current) {
          await queryClient.refetchQueries({ queryKey: tcRunKeys.detail(tcIdRef.current) });
          const fresh = queryClient.getQueryData<TcRunDetailResponse>(
            tcRunKeys.detail(tcIdRef.current)
          );
          const freshAppraisee = fresh?.appraisees.find(
            (a) => a.appraisalId === appraiseeRef.current.appraisalId
          );
          if (freshAppraisee) rowVersion = freshAppraisee.rowVersion;
          needsRefreshRef.current = false;
        }

        // 3. Run the caller's mutation. Whether it succeeds or throws (409), the next
        //    caller must refetch — our rowVersion is now stale either way.
        try {
          return await mutationFn(rowVersion);
        } finally {
          needsRefreshRef.current = true;
        }
      };

      const p = run();
      pendingRef.current = p;

      // Clear the head when we're the current pending and we've settled, so that a
      // fresh caller after a full quiescence can start immediately without waiting on
      // a long-resolved promise.
      p.catch(() => undefined).finally(() => {
        if (pendingRef.current === p) pendingRef.current = null;
      });

      return p;
    },
    [queryClient]
  );

  return (
    <TcRunAppraiseeMutationQueueContext.Provider value={{ runSerialized }}>
      {children}
    </TcRunAppraiseeMutationQueueContext.Provider>
  );
}
